import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { usePathname, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { InputText } from '@/components/form';
import { Options } from '@/components/ui/combobox';
import { GET_LAWYER_CASE_PHASES, GET_LAWYER_CASE_STATUSES } from '@/api';
import Icons from '@/const/icons';
import clsx, { ClassValue } from 'clsx';
import { casesLawyerService } from '@/api/casesLawyer';
import ModalFilterCases from './ModalFilterCases';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import {
	Button,
	Icon,
	LabelLawyerState,
	Typography,
	TypographyType,
} from '@/components/common';
import {
	ComboboxMultiple,
	OptionsMultiple,
} from '@/components/ui/comboboxMultiple';
import AppRoutes from '@/const/routes';
import { CaseLawyerStatus, CasePhases } from '@/interfaces/casesLawyer';

interface Props {
	sizeText?: TypographyType;
	classNameIcon?: string;
	classNameInput?: ClassValue;
	classNameCombo?: string;
	total: number | undefined;
	totalItems: number | undefined;
	totalRecords: number | null;
}

export const FiltersLawyer: React.FC<Props> = ({
	total,
	totalItems,
	totalRecords,
	sizeText = 'regular-16',
	classNameInput,
	classNameIcon,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const width = useDeviceWidth();
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const { replace } = useRouter();
	const { register, reset } = useForm<any>({
		defaultValues: {
			search: searchParams.get('search') || '',
		},
	});

	const params = new URLSearchParams(searchParams);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const [filters, setFilters] = useState<OptionsMultiple[]>([]);
	const [optionSelected, setOptionSelected] = useState<string | undefined>(
		searchParams.get('status') ||
			searchParams.get('phase') ||
			(searchParams.get('alphabetical') ? 'alphabetical' : undefined)
	);

	useEffect(() => {
		reset({ search: searchParams.get('search') || '' });
		const status = searchParams.get('status');
		const phase = searchParams.get('phase');
		const alphabetical = searchParams.get('alphabetical');

		if (status) {
			setOptionSelected(status);
		} else if (phase) {
			setOptionSelected(phase);
		} else if (alphabetical) {
			setOptionSelected('alphabetical');
		} else {
			setOptionSelected(undefined);
		}
	}, [searchParams, reset]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	const { data: caseStatuses } = useQuery({
		queryKey: [GET_LAWYER_CASE_STATUSES],
		queryFn: () => casesLawyerService.getCaseStatuses(),
	});

	const { data: casePhases } = useQuery({
		queryKey: [GET_LAWYER_CASE_PHASES],
		queryFn: () => casesLawyerService.getCasePhases(),
	});

	const getIcon = (name: string) => {
		let src = '';
		if (name === 'Investigation') {
			src = Icons.search;
		} else if (name === 'Applicant Intake') {
			src = Icons.tag;
		} else if (name === 'Active Case') {
			src = Icons.folderOpen;
		} else if (name === 'Settlement') {
			src = Icons.bank;
		}

		return <Icon src={src} width={18} height={18} className="text-primary" />;
	};

	useEffect(() => {
		if (caseStatuses?.data && casePhases?.data) {
			const casesStatusList =
				caseStatuses?.data.map(({ id, name }: CaseLawyerStatus) => ({
					value: `${id}-status`,
					label: name,
					icon: <LabelLawyerState status={name} withLabel={false} />,
				})) || [];

			const casesPhasesList =
				casePhases?.data.map(({ id, name }: CasePhases) => ({
					value: `${id}-phase`,
					label: name,
					icon: getIcon(name),
				})) || [];

			const initOptions: Options[] = [
				{
					label: 'View All',
					value: 'View All',
					icon: (
						<Icon
							src={Icons.list}
							width={18}
							height={18}
							className="text-primary"
						/>
					),
				},

				{
					label: 'Alphabetical Order',
					value: 'alphabetical',
					icon: (
						<Icon
							src={Icons.alphabeticalOrder}
							width={18}
							height={18}
							className="text-primary"
						/>
					),
				},
			];

			const groupFilters: OptionsMultiple[] = [
				{
					title: '',
					options: initOptions,
				},
				{
					title: 'Case Phase',
					options: casesPhasesList,
				},
				{
					title: 'Case Status',
					options: casesStatusList,
				},
			];
			setFilters(groupFilters);
		}
	}, [caseStatuses, casePhases]);

	const clearFilters = () => {
		params.delete('status');
		params.delete('phase');
		params.delete('alphabetical');
		setOptionSelected(undefined);
		replaceUrl();
	};

	const handleAlphabeticalOrder = () => {
		const isAlphabetical = params.get('alphabetical');
		if (isAlphabetical) {
			params.delete('alphabetical');
		} else {
			params.set('alphabetical', 'true');
		}
		replaceUrl();
	};

	const handleChange = (group: string, id: string | undefined) => {
		if (id === 'view all') {
			clearFilters();
			return;
		}

		if (id === 'alphabetical') {
			handleAlphabeticalOrder();
			return;
		}

		setOptionSelected(id);

		if (group === 'Case Status') {
			if (id) {
				params.delete('phase');
				params.set('status', id);
			} else {
				params.delete('status');
			}
		}

		if (group === 'Case Phase') {
			if (id) {
				params.delete('status');
				params.set('phase', id);
			} else {
				params.delete('phase');
			}
		}

		replaceUrl();
	};

	const replaceUrl = () => {
		params.set('page', '1');
		replace(`${pathname}?${params.toString()}`);
	};

	const handleSearch = useDebouncedCallback((value: string) => {
		params.set('page', '1');
		if (value) {
			params.set('search', value);
		} else {
			params.delete('search');
		}
		replace(`${pathname}?${params.toString()}`);
	}, 300);

	return (
		<>
			<div className="flex flex-col lg:flex-row lg:justify-between items-center gap-4">
				<div className="flex flex-col lg:flex-row items-baseline gap-4 w-full">
					<InputText
						ref={inputRef}
						name="search"
						register={register}
						placeholder="Search"
						iconLeft={Icons.search}
						className="w-full lg:max-w-[417px]"
						classNameIcon={clsx(
							'!top-[10%] !size-4 text-opacity-black',
							classNameIcon
						)}
						classNameInput={clsx(
							'border-none xl:w-[417px] h-[37px] !pl-10 !-mt-2',
							classNameInput
						)}
						onChange={({ target }) => handleSearch(target.value)}
					/>
					<div
						className="pr-2"
						onClick={() => {
							if (width < 640) {
								setIsModalOpen(true);
							}
						}}
					>
						<ComboboxMultiple
							open={isOpen}
							placeholder="View All"
							options={filters}
							value={optionSelected}
							isDropdown={false}
							classNameCommandList="!max-h-full"
							classNameButton="!p-2"
							classNameArrow="!w-4 !h-4 !mt-0"
							setOpen={(value) => {
								if (width > 640) setIsOpen(value);
							}}
							setValue={(key, value) => handleChange(key, value)}
						/>
					</div>
					<ShowingLabel
						sizeText={sizeText}
						total={total}
						totalItems={totalItems}
						paramSize={params.size}
						totalRecords={totalRecords}
					/>
				</div>
				<Button
					href={AppRoutes.LAWYERAPP_CREATE_CASE}
					label="New Case"
					size="fit"
					decoration="primary"
					className="whitespace-nowrap h-12"
					classNameButton="!w-4 !h-4"
					iconLeft
					icon={Icons.plus}
				/>
			</div>
			<ModalFilterCases isOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
		</>
	);
};

interface ShowingLabelProps {
	sizeText: TypographyType;
	total?: number;
	totalItems?: number;
	paramSize: number;
	totalRecords: number | null;
}

export const ShowingLabel: React.FC<ShowingLabelProps> = ({
	sizeText,
	total,
	totalItems,
	paramSize,
	totalRecords,
}) => {
	return (
		<Typography type={sizeText} className="opacity-60 text-right">
			{paramSize > 1
				? `Showing ${totalItems || '0'} ${
						totalItems && totalItems === 1 ? 'result' : 'results'
				  } of ${totalRecords || '0'} ${
						totalRecords && totalRecords === 1 ? 'case' : 'cases'
				  }.`
				: `Showing ${total || '0'} of ${totalItems || '0'} ${
						totalItems && totalItems === 1 ? 'case' : 'cases'
				  }.`}
		</Typography>
	);
};
